<script setup lang="ts">
defineOptions({
	name: "teacher.profile",
});
definePage({
	name: "teacher.profile",
	redirect: "/teacher/profile/general-info",
	meta: {
		warnReloadOnChangeLanguage: true,
	},
});

import IcOutlineInfo from "~icons/ic/outline-info";
import IcBaselineHistoryEdu from "~icons/ic/baseline-history-edu";
import IcBaselineSchool from "~icons/ic/baseline-school";
import IcRoundWorkOutline from "~icons/ic/round-work-outline";
import IcOutlineInsertDriveFile from "~icons/ic/outline-insert-drive-file";
import { useSessionStore } from "@/store/session";
import api from "@/api";
import guideImage from "@/assets/image/school-setup-guide.png";
import {
	debounce,
	Errors,
	getSessionStorageReac,
	// getProfileSetupErrorNextPage,
	handleNewExperiences,
	// isEmptyExperience,
	setSessionStorageReac,
	useAsyncState,
	validateDocuments,
} from "@/assets/js/helpers";
import { useProfileStore } from "@/store/profile";
import { $t } from "@/i18n";
import type { TCvExperienceEdit, TFinishProfileParams } from "@/api/types";
import { useToastStore } from "@/store/toast";
import type {
	TContractType,
	TCvCode,
	TCvExperience,
} from "@/assets/js/globalTypes";
import {
	validateTeacherProfile,
	validateTeacherInfoAndExperience,
} from "@/assets/js/teacherValidation";
import { openTeacherFinishSetupModal } from "@/components/modals/modalDefinitions";

const sessionStore = useSessionStore();
const userId = computed(() => sessionStore.userId);

const [getTeacher, isTeacherLoading, _, teacherData] =
	useAsyncState(getTeacherData);

const profileStore = useProfileStore();
const router = useRouter();
const profilePageWrapper = ref<HTMLElement | null>(null);

watch(
	teacherData,
	(newVal) => {
		if (newVal) {
			profileStore.updateProfile(newVal);
		}
	},
	{ immediate: true },
);

const showGuide = ref(false);
watch(
	() => profileStore.profile,
	(val) => {
		const greetingClosed = getSessionStorageReac("greeting-message-dismissed")
			?.value as string;
		// if (val && val.usr_profilecompleted && !val.usr_accountapproved) {
		// 	// openTeacherFinishSetupModal("finish");
		// }
		showGuide.value =
			val && !val.usr_profilecompleted && !greetingClosed ? true : false;
		// showGuide.value =
		// 	val && !val.usr_profilecompleted ? true : false;
	},
	{ immediate: true },
);
function closeGuide() {
	showGuide.value = false;
	setSessionStorageReac("greeting-message-dismissed", true);
}

async function getTeacherData() {
	const res = await api.getTeacher(userId.value);
	if (res.data.pending) profileStore.profilePending = res.data.pending;
	return res.data;
}

const route = useRoute();

const stepperValue = computed(() => {
	switch (route.name) {
		case "teacher.profile.general-info": {
			return 1;
		}
		case "teacher.profile.teaching-info": {
			return 2;
		}
		case "teacher.profile.education-background": {
			return 3;
		}
		case "teacher.profile.working-experience": {
			return 4;
		}
		case "teacher.profile.teaching-experience": {
			return 5;
		}
		case "teacher.profile.documents": {
			return 6;
		}
		default: {
			return 1;
		}
	}
});
const nextLink = computed((): any => {
	// if (profileStore.errors.any()) {
	// 	// return {
	// 	// 	name: getProfileSetupErrorNextPage(profileStore.errors as Errors) as any,
	// 	// };
	// }
	switch (stepperValue.value) {
		case 1: {
			return { name: "teacher.profile.teaching-info" };
		}
		case 2: {
			return { name: "teacher.profile.education-background" };
		}
		case 3: {
			return { name: "teacher.profile.working-experience" };
		}
		case 4: {
			return { name: "teacher.profile.teaching-experience" };
		}
		case 5: {
			return { name: "teacher.profile.documents" };
		}
		default: {
			return null;
		}
	}
});

const previousLink = computed(() => {
	switch (stepperValue.value) {
		case 2: {
			return { name: "teacher.profile.general-info" };
		}
		case 3: {
			return { name: "teacher.profile.teaching-info" };
		}
		case 4: {
			return { name: "teacher.profile.education-background" };
		}
		case 5: {
			return { name: "teacher.profile.working-experience" };
		}
		case 6: {
			return { name: "teacher.profile.teaching-experience" };
		}
		default: {
			return null;
		}
	}
});

const getFinishButtonText = computed(() =>
	profileStore.finishedProfileSetup
		? $t("app.teacher.submitForValidation", {
				entity: $t("app.base.change", 2).toLowerCase(),
			})
		: $t("app.teacher.submitForValidation", {
				entity: $t("app.base.profile").toLowerCase(),
			}),
);

function openFillAllFieldsToast() {
	toastStore.openToastError($t("app.base.fillAllFields"));
}

const callToast = debounce(openFillAllFieldsToast, 2000, true);
async function handleNextButton() {
	const page = route.name;
	const val = validateTeacherProfile(
		{ ...profileStore.profileChanges, newImage: profileStore.newImage },
		page,
	);
	if (val) {
		callToast();
		return;
	}
	router.push(nextLink.value);
}

async function handleEditExperiences(experiences: TCvExperienceEdit) {
	const newValid = Object.keys(experiences.new).length;
	const removedValid = experiences.removed && experiences.removed.length;
	const editedValid = Object.keys(experiences.edited).length;
	if (!newValid && !removedValid && !editedValid) {
		return;
	}
	try {
		const promiseArray = [];
		if (newValid) {
			promiseArray.push(api.addExperiences({ experiences: experiences.new }));
		}
		if (removedValid) {
			promiseArray.push(
				api.removeExperiences({ cve_ids: experiences.removed }),
			);
		}
		if (editedValid) {
			promiseArray.push(
				api.editExperiences({ experiences: experiences.edited }),
			);
		}
		await Promise.all(promiseArray);
		return true;
	} catch (e) {
		toastStore.openToastError(e.message);
	}
}

const requiredExpFields = {
	EDUC: ["cve_company", "cve_end", "cve_start", "cve_title"],
	PEXP: ["cve_company", "cve_end", "cve_start", "cve_title"],
	TEXP: [
		"cve_company",
		// "cve_coursecontent",
		"cve_courselevel",
		"cve_coursetitle",
		"cve_end",
		"cve_hourlyvolume",
		"cve_start",
		"cve_title",
	],
};

const sectionLabels = {
	EDUC: $t("app.teacher.profileStepper.education"),
	PEXP: $t("app.teacher.profileStepper.working-experience"),
	TEXP: $t("app.teacher.profileStepper.teaching-experience"),
};

function validateExperienceFields(data: TCvExperienceEdit) {
	const errors = {};

	for (const group in requiredExpFields) {
		const entries = data.new?.[group] || [];
		const groupErrors = [];
		const toRemove = [];

		for (const [index, item] of entries.entries()) {
			// const fieldsToCheck = requiredExpFields[group].filter((field) => {
			// 	if (field === "cve_end" && item.ongoing === true) {
			// 		return false;
			// 	}
			// 	return true;
			// });

			const missingFields = requiredExpFields[group].filter(
				(field) =>
					item[field] === null ||
					item[field] === "" ||
					item[field] === undefined,
			);

			if (
				"cve_hourlyvolume" in item &&
				(item.cve_hourlyvolume === 0 || item.cve_hourlyvolume === "0")
			) {
				missingFields.push("cve_hourlyvolume");
			}

			if (missingFields.length > 0) {
				if (missingFields.length === requiredExpFields[group].length) {
					toRemove.push(index);
				} else {
					groupErrors.push({
						index,
						missingFields,
					});
				}
			}
		}

		for (const index of toRemove.reverse()) {
			entries.splice(index, 1);
		}

		if (groupErrors.length > 0) {
			errors[group] = groupErrors;
		}
	}

	return Object.keys(errors).length > 0 ? errors : null;
}

function getExpSectionWithInvalidDates(data: TCvExperienceEdit) {
	const sections = ["new", "edited"];
	const invalidSections = new Set();

	for (const section of sections) {
		if (!data[section]) continue;

		for (const groupKey in data[section]) {
			const entries = data[section][groupKey];
			for (const entry of entries) {
				const start = new Date(entry.cve_start);
				const end = new Date(entry.cve_end);
				if (!entry.ongoing && start > end) {
					invalidSections.add(groupKey);
				}
			}
		}
	}

	return Array.from(invalidSections);
}

const toastStore = useToastStore();
const saveProfileLoading = ref(false);
async function finishSetup() {
	const params: Partial<TFinishProfileParams> = {};

	const val = validateTeacherInfoAndExperience({
		...profileStore.profileChanges,
		newImage: profileStore.newImage,
	});
	const hasInvalidSections = val.some((item) => item.valid !== null);
	if (hasInvalidSections) {
		callToast();
		return;
	}
	profileStore.errors = new Errors();

	const action = profileStore.finishedProfileSetup
		? api.editUser
		: api.finishProfile;
	const edit = profileStore.finishedProfileSetup ? true : false;
	const requiredDocumentTypes = await getDocumentTypes();
	if (!requiredDocumentTypes) {
		toastStore.openToastError($t("app.base.somethingWrong"));
		return;
	}

	let experiences: TCvExperienceEdit = null;
	if (edit) {
		experiences = {
			new: {},
			removed: [],
			edited: {},
		};
	}

	const data = edit
		? profileStore.getEditedFields()
		: profileStore.profileChanges;

	if (edit && data.usr_fullname) {
		params.usr_fullname = data.usr_fullname;
	}
	if (edit && data.usr_title) {
		params.usr_title = data.usr_title;
	}
	if (edit && data.usr_phone) {
		params.usr_phone = data.usr_phone;
	}

	if (data.cities && Array.isArray(data.cities) && data.cities.length > 0) {
		params.cities = data.cities.map((el) => el.cit_id);
	}
	if (data.languages && data.languages.length > 0) {
		params.languages_teach = data.languages.map((el) => el.lng_code);
	}
	if (data.subjects && data.subjects.length > 0) {
		params.subjects = data.subjects.map((el) => el.sfp_id);
	}
	if (data.skills && data.skills.length > 0) {
		params.skills = data.skills.map((el) => el.tag_id);
	}
	if (data.contract_types && data.contract_types.length > 0) {
		// Recheck required documents if the teacher has added a freelance contract type
		if (edit) {
			const isFreelanceAdded = isFreelanceAddedCheck(
				profileStore.profile.contract_types,
				data.contract_types,
			);
			if (isFreelanceAdded) {
				const missing_documents = validateDocuments(
					profileStore.profileChanges.files,
					requiredDocumentTypes,
				);
				if (missing_documents.length > 0) {
					toastStore.openToastError($t("app.teacher.profile.missingDocuments"));
					return;
				}
			}

			function isFreelanceAddedCheck(oldValues, newValues) {
				const hasInOld = oldValues.some(
					(item: TContractType) => item.ctt_code === "FRLN",
				);
				const hasInNew = newValues.some(
					(item: TContractType) => item.ctt_code === "FRLN",
				);
				return !hasInOld && hasInNew;
			}
		}

		params.contract_types = data.contract_types.map((el) => el.ctt_code);
	}
	if (profileStore.newImage) {
		params.usr_avatar = profileStore.newImage;
	}

	if (!edit && data.experiences) {
		const res = {} as Partial<Record<TCvCode, TCvExperience[]>>;
		for (const el of Object.keys(data.experiences)) {
			if (data.experiences[el] && data.experiences[el].length === 0) {
				delete data.experiences[el];
			}
			if (!data.experiences[el]) continue;
			const experiences: TCvExperience[] = [];

			for (const item of data.experiences[el]) {
				const required = requiredExpFields[el as TCvCode];
				if (!required) continue;

				if (item.ongoing) {
					item.cve_end = "3000-01-01";
				}

				const present = required.filter((field) => {
					const value = item[field];
					if (field === "cve_hourlyvolume") {
						return value !== null && value !== "" && value !== 0;
					}
					// if (field === "cve_end" && item.ongoing) {
					// 	return true;
					// }
					return value !== null && value !== "";
				});

				if (present.length === 0) {
					continue;
				} else if (present.length < required.length) {
					const label = sectionLabels[el] || el;
					toastStore.openToastError(
						$t("app.teacher.profile.expRequiredFields", {
							type: label.toLowerCase(),
						}),
					);

					return;
				}
				experiences.push(item);
			}
			res[el as TCvCode] = handleNewExperiences(experiences);
		}

		params.experiences =
			Object.keys(res).length === 0 ? null : JSON.stringify(res);
	} else if (data.experiences) {
		if ("new" in data.experiences) {
			for (const el of data.experiences.new) {
				const newWithoutId = { ...el };
				delete newWithoutId.cve_id;
				if (newWithoutId?.cve_end === null && newWithoutId?.cve_start) {
					newWithoutId.cve_end = "3000-01-01";
				}
				if (!experiences.new[el.cvt_code]) {
					experiences.new[el.cvt_code] = [];
				}
				experiences.new[el.cvt_code].push(newWithoutId);
			}
		}
		if ("removed" in data.experiences) {
			for (const el of data.experiences.removed) {
				if (!experiences.removed) {
					experiences.removed = [];
				}
				experiences.removed.push(el.cve_id);
			}
		}
		if ("edited" in data.experiences) {
			for (const el of data.experiences.edited) {
				if (!experiences.edited[el.cvt_code]) {
					experiences.edited[el.cvt_code] = [];
				}
				const objectWithoutCode = { ...el };
				delete objectWithoutCode.cvt_code;
				if (
					objectWithoutCode?.cve_end === null &&
					objectWithoutCode?.cve_start
				) {
					objectWithoutCode.cve_end = "3000-01-01";
				}
				experiences.edited[el.cvt_code].push(objectWithoutCode);
			}
		}
	}

	if (data.usr_description) {
		params.usr_description = data.usr_description;
	}
	if (data.usr_professionalexperience) {
		params.usr_professionalexperience = data.usr_professionalexperience;
	}
	if (data.usr_experience) {
		params.usr_experience = data.usr_experience;
	}
	if (data.usr_hourlyrate) {
		if (data.usr_hourlyrate > 999) {
			toastStore.openToastError($t("app.teacher.profile.maximumHourlyRate"));
			return;
		}
		params.usr_hourlyrate = data.usr_hourlyrate;
	}
	if (data.usr_remotework !== undefined && data.usr_remotework !== null) {
		params.usr_remotework = !!data.usr_remotework;
	}

	if (data.files) {
		if (!edit) {
			const missing_documents = validateDocuments(
				data.files,
				requiredDocumentTypes,
			);
			if (missing_documents.length > 0) {
				toastStore.openToastError($t("app.base.fillAllFields"));
				return;
			}
		}
		params.files = data.files.map((el) => el.fil_id);
	}

	const formData = new FormData();

	// Check if any required fields are missing in the experience sections (EDUC, PEXP, TEXP)
	if (experiences) {
		console.log(experiences);
		const result = validateExperienceFields(experiences);

		if (result) {
			for (const section in result) {
				const label = sectionLabels[section] || section;
				toastStore.openToastError(
					$t("app.teacher.profile.expRequiredFields", {
						type: label.toLowerCase(),
					}),
				);
			}
			return;
		}

		const invalidDateSections = getExpSectionWithInvalidDates(experiences);
		if (invalidDateSections.length > 0) {
			for (const section of invalidDateSections) {
				const label =
					sectionLabels[section as keyof typeof sectionLabels] || section;
				toastStore.openToastError(
					$t("app.teacher.profile.expInvalidDate", {
						type: label.toLowerCase(),
					}),
				);
			}
			return;
		}
	}

	for (const key of Object.keys(params)) {
		if (Array.isArray(params[key])) {
			formData.append(key, JSON.stringify(params[key]));
		} else {
			formData.append(key, params[key]);
		}
	}

	try {
		saveProfileLoading.value = true;
		await action(formData);
		if (experiences) {
			await handleEditExperiences(experiences);
		}

		getTeacher();

		openTeacherFinishSetupModal(edit ? "edit" : "finish");
	} catch (e) {
		if (e.response?.data?.message) {
			toastStore.openToastError(e.response.data.message);
		} else {
			toastStore.openToastError(e.message);
		}
	} finally {
		saveProfileLoading.value = false;
	}
}

async function getDocumentTypes() {
	try {
		const documentTypesRequired = await api.getFileTypes(
			profileStore.profileChanges.contract_types,
		);
		return documentTypesRequired.data.data;
	} catch (e) {
		console.error(e);
	}
}

watch(
	() => route.fullPath,
	() => {
		nextTick(() => {
			if (profilePageWrapper.value)
				profilePageWrapper.value.scrollTo({ top: 0 });
		});
	},
	{ immediate: true },
);

onMounted(() => {
	getTeacher();
});
</script>

<template>
	<div
		id="profile-page"
		ref="profilePageWrapper"
	>
		<template v-if="!isTeacherLoading">
			<div class="stepper-wrap">
				<p-stepper
					:value="stepperValue"
					linear
				>
					<p-step-list>
						<p-step
							v-slot="{ active }"
							:value="1"
							as-child
						>
							<teacher-stepper-item
								:label="$t('app.teacher.profileStepper.general')"
								route="teacher.profile.general-info"
								:active="active"
								:icon="IcOutlineInfo"
							/>
						</p-step>
						<p-step
							v-slot="{ active }"
							:value="2"
							as-child
						>
							<teacher-stepper-item
								:label="$t('app.teacher.profileStepper.teaching')"
								route="teacher.profile.teaching-info"
								:active="active"
								:icon="IcBaselineHistoryEdu"
							/>
						</p-step>
						<p-step
							v-slot="{ active }"
							:value="3"
							as-child
						>
							<teacher-stepper-item
								:label="$t('app.teacher.profileStepper.education')"
								route="teacher.profile.education-background"
								:active="active"
								:icon="IcBaselineSchool"
							/>
						</p-step>
						<p-step
							v-slot="{ active }"
							:value="4"
							as-child
						>
							<teacher-stepper-item
								:label="$t('app.teacher.profileStepper.working-experience')"
								route="teacher.profile.working-experience"
								:active="active"
								:icon="IcRoundWorkOutline"
							/>
						</p-step>
						<p-step
							v-slot="{ active }"
							:value="5"
							as-child
						>
							<teacher-stepper-item
								:label="$t('app.teacher.profileStepper.teaching-experience')"
								route="teacher.profile.teaching-experience"
								:active="active"
								:icon="IcRoundWorkOutline"
							/>
						</p-step>
						<p-step
							v-slot="{ active }"
							:value="6"
							as-child
						>
							<teacher-stepper-item
								:label="$t('app.teacher.profileStepper.documents')"
								route="teacher.profile.documents"
								:active="active"
								last
								:icon="IcOutlineInsertDriveFile"
							/>
						</p-step>
					</p-step-list>
				</p-stepper>
			</div>
			<div class="spinner-wrap">
				<p-progress-spinner v-if="isTeacherLoading"></p-progress-spinner>
			</div>
			<router-view class="sub-page" />
			<div class="button-wrap">
				<div class="button-wrap-top">
					<span>
						<p-button
							v-if="previousLink"
							as="router-link"
							outlined
							:to="previousLink"
						>
							<i-ic-chevron-left></i-ic-chevron-left>
							{{ $t("app.base.previous") }}
						</p-button>
					</span>
					<p-button
						v-if="nextLink"
						:pt="{
							root: {
								class: 'next-button-root',
							},
						}"
						@click="handleNextButton"
					>
						{{ $t("app.base.next") }}
						<i-ic-chevron-right></i-ic-chevron-right>
					</p-button>
				</div>
				<div class="button-wrap-bottom">
					<p-button
						v-if="profileStore.profile?.usr_profilecompleted || !nextLink"
						:disabled="
							saveProfileLoading ||
							(profileStore.finishedProfileSetup && !profileStore.hasChanges)
						"
						:severity="nextLink ? 'secondary' : 'primary'"
						:pt="{
							root: {
								class: 'finish-button-root',
							},
						}"
						@click="finishSetup"
					>
						{{ getFinishButtonText }}
						<i-ic-baseline-save></i-ic-baseline-save>
					</p-button>
				</div>
			</div>
			<school-setup-guide
				v-if="showGuide"
				closable
				:is-modal="true"
				:image="guideImage"
				:text="$t('app.teacher.profile.profileSetupFirstTime')"
				@close="closeGuide()"
			></school-setup-guide>
		</template>
		<template v-else>
			<div class="spinner-wrap">
				<p-progress-spinner></p-progress-spinner>
			</div>
		</template>
	</div>
</template>

<style lang="scss" scoped>
#profile-page {
	padding: 1rem 0 0;
	box-sizing: border-box;
	height: calc(100dvh - $nav-top-bar - 1px);
	overflow-y: auto;

	.spinner-wrap {
		display: flex;
		justify-content: center;
	}
	.stepper-wrap {
		padding: 0 1rem;
		min-height: 62px;

		@media screen and (width >= 768px) {
			min-height: 107px;
		}

		@media screen and (width >= 1400px) {
			padding: 0 57px 0 23px;
		}
	}
	.button-wrap {
		display: flex;
		flex-direction: column;
		padding: 1rem 1rem 2rem;
		gap: 20px;
		background-color: $daidai-light-blue;

		.button-wrap-top {
			flex: 1;
			width: 100%;
			display: flex;
			justify-content: space-between;
		}

		.button-wrap-bottom {
			width: 100%;
			display: flex;
			flex-direction: row-reverse;

			.finish-button-root {
				width: 100%;
				white-space: nowrap;
			}
		}

		.faico {
			font-size: 1.5rem;
		}
	}

	@media screen and (width >= 500px) {
		.button-wrap {
			.button-wrap-bottom {
				.finish-button-root {
					width: fit-content;
				}
			}
		}
	}

	@media screen and (width >= 768px) {
		// padding: 1rem 2.5rem;

		.button-wrap {
			display: flex;
			flex-direction: row;
			background-color: unset;

			.button-wrap-bottom {
				width: fit-content;
			}
		}
	}
}
</style>
